import {
  useWSModal,
  useWSModalCustom,
  WSButton,
  WSControl,
  WSControlGroup,
  WSElement,
  WSInfoBox,
  WSList,
  WSModal,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { IFilingSettings1099 } from "@wingspanhq/payments/dist/interfaces";
import { IFilingSettings1099ByYear } from "@wingspanhq/payments/dist/interfaces/paymentSettings";
import React, { useCallback, useState } from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../query/hooks/helpers";
import { openInNewTab } from "../../../shared/utils/openInNewTab";
import { WS_LINKS } from "../../../types/wsLinks";
import { TaxFormType } from "@wingspanhq/payments/dist/interfaces/taxForm";
import { MISCDescription } from "./MISCDescription";
import { useUpdatePayrollSettings } from "../../../query/payments/mutations";
import { QUERY_PAYROLL_SETTINGS } from "../../../query/payments/keys";

type Value = "yes" | "no";

type Props = {
  onClose: () => void;
  period: keyof IFilingSettings1099<IFilingSettings1099ByYear>;
  onSubmit: (value: Value) => void;
};

export const ModalOptInTaxFilling: React.FC<Props> = props => {
  const [value, setValue] = useState<Value>("yes");
  const [taxFormType, setTaxFormType] = useState<TaxFormType>();

  const userId = useUserId();

  const [updatePayrollSettings, updatePayrollSettingsMeta] =
    useUpdatePayrollSettings(userId, {
      dependencies: [],
      awaitDependencies: [QUERY_PAYROLL_SETTINGS]
    });

  return (
    <WSModal
      divider
      title={props.period + " Tax filing"}
      onClose={props.onClose}
      actions={{
        alignment: "fill",
        buttons: [
          {
            disabled: value === "yes" && !taxFormType,
            label: "Confirm & continue",
            loading: updatePayrollSettingsMeta.isLoading,
            onAsyncClick: async () => {
              const filingSettings1099ByYear: IFilingSettings1099ByYear =
                value === "yes"
                  ? {
                      filingEnabled: true,
                      taxFormType: taxFormType
                    }
                  : { filingEnabled: false };

              await updatePayrollSettings(
                {
                  calculationSettings1099: {
                    filingSettings: {
                      [props.period]: filingSettings1099ByYear
                    }
                  }
                },
                {
                  onSuccess: () => {
                    props.onSubmit(value);
                    props.onClose();
                  }
                }
              );
            }
          }
        ]
      }}
    >
      <WSList gap="XL">
        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="S">
            Do you plan on filing {props.period} Form 1099s through Wingspan?
          </WSText.ParagraphSm>

          <WSInfoBox>
            If not opted-in by the January 31st filing deadline, the dashboard
            will be set to opted-out of tax filing for the year.
          </WSInfoBox>
        </WSElement>

        <WSControlGroup
          name="enable"
          type="radio"
          value={value}
          onChange={newValue => {
            setValue(newValue as Value);
          }}
          chip
          options={[
            {
              value: "yes",
              label: `Yes, I am filing ${props.period} Form 1099s through Wingspan`,
              description: (
                <WSList gap="L">
                  <p>
                    <b>
                      Your tax filing dashboard will be updated for the current
                      tax year.
                    </b>{" "}
                    Review the Wingspan filing process and preparation guides
                    from your dashboard to ensure you have all the contractor
                    and payments information in Wingspan to generate and file
                    tax forms. Learn more about the filing process{" "}
                    <a
                      href={WS_LINKS.taxFillingOverview}
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                    .
                  </p>
                  <WSSelect
                    name="filingType"
                    value={taxFormType || ""}
                    label="1099 type"
                    required
                    helperText="Choose between 1099-NEC or 1099-MISC (Box 6). Once set, the form type will be locked in for the tax year."
                    onChange={value => setTaxFormType(value as TaxFormType)}
                    options={[
                      {
                        value: TaxFormType.Form1099Nec,
                        label: "1099-NEC"
                      },
                      {
                        value: TaxFormType.Form1099Misc,
                        label: "1099-MISC (Box 6)"
                      }
                    ]}
                  />

                  {taxFormType === TaxFormType.Form1099Misc ? (
                    <MISCDescription />
                  ) : null}
                </WSList>
              ),
              activeDescription: (
                <>
                  <p>
                    <b>Contractor experience:</b>
                  </p>
                  <p>
                    All contractors you have added will be invited to sign-up
                    for a Wingspan account. Based on the eligibility criteria,
                    your contractors will be informed about whether a 1099 will
                    be filed for them or not through Wingspan.
                  </p>
                </>
              )
            },
            {
              value: "no",
              label: `No, I am not filing ${props.period} Form 1099s through Wingspan`,
              description: (
                <p>
                  <b>
                    Your tax filing dashboard will be updated to reflect opting
                    out of filing for the current tax year (this cannot be
                    reverted after the tax filing deadline).
                  </b>{" "}
                  You can still access previous years tax forms and you’ll have
                  the option to opt-in to future filing years when the time
                  arrives.
                </p>
              ),
              activeDescription: (
                <>
                  <p>
                    <b>Contractor experience:</b>
                  </p>
                  <p>
                    All contractors will be informed that they will not receive
                    a 1099 form for the current filing year ({props.period})
                    through Wingspan.
                  </p>
                </>
              )
            }
          ]}
        />

        <WSText.ParagraphSm color="gray500" mb="S">
          If not opted-in by the filing deadline, the dashboard will be set to
          opted-out of tax filing for the year.
        </WSText.ParagraphSm>

        <WSErrorMessage
          contextKey="TaxFilling"
          error={updatePayrollSettingsMeta.error}
        />
      </WSList>
    </WSModal>
  );
};

export const ModalTaxFillingReminders: React.FC<{
  onClose: () => void;
  period: keyof IFilingSettings1099<IFilingSettings1099ByYear>;
}> = props => {
  const [consent, setConsent] = useState(false);
  const userId = useUserId();
  const [updatePayrollSettings, updatePayrollSettingsMeta] =
    useUpdatePayrollSettings(userId, {
      dependencies: [],
      awaitDependencies: [QUERY_PAYROLL_SETTINGS]
    });

  return (
    <WSModal
      divider
      onClose={props.onClose}
      title="Important tax filing reminders"
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Continue",
            disabled: !consent,
            onAsyncClick: async () => {
              await updatePayrollSettings(
                {
                  calculationSettings1099: {
                    filingSettings: {
                      [props.period]: {
                        filingEnabled: true
                      } as IFilingSettings1099ByYear
                    }
                  }
                },
                {
                  onSuccess: () => {
                    props.onClose();
                  }
                }
              );
            },
            loading: updatePayrollSettingsMeta.isLoading
          }
        ]
      }}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500" mb="S">
          Key steps to take to in advance of tax filing on the Wingspan platform
          to set your contractors up for success this tax filing season:
        </WSText.ParagraphSm>

        <WSInfoBox>
          <p>
            <b>Attend a 1099 filing training</b>
            <ul>
              <li>
                Contact your Wingspan account manager to attend a live 1099
                training session or watch a recorded playback (if available).
              </li>
            </ul>
          </p>

          <p>
            <b>Review the contractor communication guide</b>
            <ul>
              <li>
                Review the{" "}
                <a
                  href={WS_LINKS.communicationTimeline}
                  target="_blank"
                  rel="noreferrer"
                >
                  communication timeline
                </a>
                . Preparing your contractors for messages from Wingspan helps to
                make a smoother experience during tax season.
              </li>
            </ul>
          </p>

          <p>
            <b>Review your responsibilities</b>
            <ul>
              <li>
                Review your responsibilities in detail and complete the
                acknowledgement.
              </li>
            </ul>
          </p>

          <WSButton.Secondary
            fullWidth
            onClick={() => {
              openInNewTab(WS_LINKS.reviewPayerResponsibilities);
            }}
            mb="L"
            rightIcon="open-tab"
          >
            Review payer responsibilities
          </WSButton.Secondary>

          <WSControl
            type="checkbox"
            name="consent"
            value={consent}
            onChange={setConsent}
            label="By checking this box, I confirm that I have reviewed and agree with the payer responsibilities."
            size="S"
          />
        </WSInfoBox>

        <WSErrorMessage
          contextKey="TaxFilling"
          error={updatePayrollSettingsMeta.error}
        />
      </WSList>
    </WSModal>
  );
};

export const useOptInTaxFilling = () => {
  const modalOptInTaxFilling = useWSModalCustom(ModalOptInTaxFilling);
  const modalTaxFillingReminders = useWSModal(ModalTaxFillingReminders);

  return useCallback((year: number) => {
    const period =
      `${year}` as keyof IFilingSettings1099<IFilingSettings1099ByYear>;

    modalOptInTaxFilling.open({
      period,
      onSubmit: value => {
        if (value === "yes") {
          modalOptInTaxFilling.close();
          modalTaxFillingReminders.open({
            period
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
